import type { InternalRefetchQueryDescriptor } from '@apollo/client';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useCallback, useState } from 'react';

import {
  DeactivateFollowupMessageDocument,
  GetDirectVisitorMessagesDocument,
  GetLineVisitorMessagesDocument,
  GetOutpatientQuestionnaireEntryDetailDocument,
  GetPatientMessagesDocument,
  GetWebVisitorMessagesDocument,
} from '~/graphql/generated/graphql';
import { getNode } from '~/graphql/utility';
import { deleteMedicationFollowupMessageModalState } from '~/state/partials/delete_medication_followup_message_modal/atoms';
import { profileDrawerState } from '~/state/partials/patient_profile_drawer/atoms';
import {
  lineVisitorProfileDrawerState,
  outpatientQuestionnaireEntryProfileDrawerState,
  webVisitorProfileDrawerState,
} from '~/state/reception/atoms';

export const useDeactivateFollowupMessage = () => {
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const patientDrawerState = useAtomValue(profileDrawerState);
  const webVisitorDrawerState = useAtomValue(webVisitorProfileDrawerState);
  const lineVisitorDrawerState = useAtomValue(lineVisitorProfileDrawerState);
  const outpatientQuestionnaireEntryDrawerState = useAtomValue(
    outpatientQuestionnaireEntryProfileDrawerState,
  );

  const resetDeleteMedicationFollowupMessageModalState = useResetAtom(
    deleteMedicationFollowupMessageModalState,
  );

  const [deactivateMessage] = useMutation(DeactivateFollowupMessageDocument);
  const [getOutpatientQuestionnaireEntry] = useLazyQuery(
    GetOutpatientQuestionnaireEntryDetailDocument,
    {
      fetchPolicy: 'network-only',
    },
  );

  const deactivate = useCallback(
    async (messageId: string) => {
      setIsDeactivating(true);
      setError(null);

      const refetchQueries: InternalRefetchQueryDescriptor[] = [];

      if (webVisitorDrawerState.isOpen && webVisitorDrawerState.webVisitorId) {
        refetchQueries.push({
          query: GetWebVisitorMessagesDocument,
          variables: { id: webVisitorDrawerState.webVisitorId },
        });
      }

      if (lineVisitorDrawerState.isOpen && lineVisitorDrawerState.lineVisitorId) {
        refetchQueries.push({
          query: GetLineVisitorMessagesDocument,
          variables: { id: lineVisitorDrawerState.lineVisitorId },
        });
      }

      if (patientDrawerState.isOpen && patientDrawerState.patientId) {
        refetchQueries.push({
          query: GetPatientMessagesDocument,
          variables: { id: patientDrawerState.patientId },
        });
      }

      if (
        outpatientQuestionnaireEntryDrawerState.isOpen &&
        outpatientQuestionnaireEntryDrawerState.outpatientQuestionnaireEntryId
      ) {
        const result = await getOutpatientQuestionnaireEntry({
          variables: {
            id: outpatientQuestionnaireEntryDrawerState.outpatientQuestionnaireEntryId,
          },
        });
        const outpatientQuestionnaireEntry = getNode(result.data, 'OutpatientQuestionnaireEntry');
        const lineVisitorId = outpatientQuestionnaireEntry?.lineVisitor?.id;
        const directVisitorId = outpatientQuestionnaireEntry?.directVisitor?.id;

        if (lineVisitorId) {
          refetchQueries.push({
            query: GetLineVisitorMessagesDocument,
            variables: { id: lineVisitorId },
          });
        }
        if (directVisitorId) {
          refetchQueries.push({
            query: GetDirectVisitorMessagesDocument,
            variables: { id: directVisitorId },
          });
        }
      }

      try {
        await deactivateMessage({
          variables: {
            input: {
              medicationFollowupMessageId: messageId,
            },
          },
          refetchQueries,
        });
        resetDeleteMedicationFollowupMessageModalState();
      } catch (error) {
        setError(error?.message || 'エラーが発生しました');
      }
      setIsDeactivating(false);
    },
    [
      deactivateMessage,
      getOutpatientQuestionnaireEntry,
      lineVisitorDrawerState.isOpen,
      lineVisitorDrawerState.lineVisitorId,
      outpatientQuestionnaireEntryDrawerState.isOpen,
      outpatientQuestionnaireEntryDrawerState.outpatientQuestionnaireEntryId,
      patientDrawerState.isOpen,
      patientDrawerState.patientId,
      resetDeleteMedicationFollowupMessageModalState,
      webVisitorDrawerState.isOpen,
      webVisitorDrawerState.webVisitorId,
    ],
  );

  return {
    isDeactivating,
    error,
    deactivate,
  };
};
